// client/src/features/salesCoach/components/MemoryRules.jsx
import React, { useState, useEffect } from "react";
import { salesCoachApi } from "../api/salesCoachApi";
import {
  Trash2,
  Edit,
  ToggleLeft,
  ToggleRight,
  Check,
  X,
  ShieldAlert,
} from "lucide-react";
import Loader from "@/shared/components/ui/Loaders/Loader";

const MemoryRules = ({ trainingId, initialRules = null }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [newRuleText, setNewRuleText] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (initialRules) {
      // If rules are passed from an end session extraction
      setRules(initialRules);
    } else {
      loadRules();
    }
  }, [trainingId, initialRules]);

  const loadRules = async () => {
    setLoading(true);
    try {
      const data = await salesCoachApi.fetchRules(trainingId);
      setRules(data);
    } catch (err) {
      console.error("Failed to load rules:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (rule) => {
    try {
      const updated = await salesCoachApi.updateRule(rule.id, {
        isEnabled: !rule.is_enabled,
      });
      setRules((prev) => prev.map((r) => (r.id === rule.id ? updated : r)));
    } catch (err) {
      console.error("Failed to toggle rule:", err);
    }
  };

  const handleStartEdit = (rule) => {
    setEditingId(rule.id);
    setEditText(rule.rule_text);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const handleSaveEdit = async (ruleId) => {
    if (!editText.trim()) return;
    try {
      const updated = await salesCoachApi.updateRule(ruleId, {
        ruleText: editText,
      });
      setRules((prev) => prev.map((r) => (r.id === ruleId ? updated : r)));
      handleCancelEdit();
    } catch (err) {
      console.error("Failed to save rule edit:", err);
    }
  };

  const handleDelete = async (ruleId) => {
    if (window.confirm("Are you sure you want to delete this coaching rule?")) {
      try {
        await salesCoachApi.deleteRule(ruleId);
        setRules((prev) => prev.filter((r) => r.id !== ruleId));
      } catch (err) {
        console.error("Failed to delete rule:", err);
      }
    }
  };

  const handleAddRule = async (e) => {
    e.preventDefault();
    if (!newRuleText.trim()) return;

    try {
      const newRule = await salesCoachApi.createRule(trainingId, newRuleText);
      setRules((prev) => [newRule, ...prev]);
      setNewRuleText("");
      setIsAdding(false);
    } catch (err) {
      console.error("Failed to add custom rule:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader text="Loading rules..." />
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex justify-between items-center pb-4 border-b border-gray-100">
        <h3 className="text-lg font-bold text-gray-800 flex items-center">
          <ShieldAlert className="w-5 h-5 mr-2 text-secondary" />
          Active Coaching Instructions ({rules.length})
        </h3>
        <button
          onClick={() => setIsAdding(!isAdding)}
          className="inline-flex items-center text-xs font-bold text-secondary hover:text-secondary"
        >
          {isAdding ? "Cancel" : "+ Add Custom Rule"}
        </button>
      </div>

      {isAdding && (
        <form onSubmit={handleAddRule} className="mt-4 flex gap-2">
          <input
            type="text"
            required
            placeholder="Type custom rule (e.g. Always mention warranty first)"
            value={newRuleText}
            onChange={(e) => setNewRuleText(e.target.value)}
            className="flex-1 rounded-xl border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:border-secondary"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-secondary text-white rounded-xl hover:bg-secondary-light text-sm font-semibold transition-all shadow-sm"
          >
            Add Rule
          </button>
        </form>
      )}

      <div className="mt-4 space-y-3">
        {rules.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">
            No coaching rules extracted. Click 'Train Coach' to speak guidelines
            to the AI.
          </p>
        ) : (
          rules.map((rule) => (
            <div
              key={rule.id}
              className={`p-4 rounded-xl border transition-all flex items-center justify-between gap-4 ${
                rule.is_enabled
                  ? "bg-secondary-ultra-light/20 border-[#207C9730]"
                  : "bg-gray-50/50 border-gray-200 opacity-60"
              }`}
            >
              <div className="flex-1">
                {editingId === rule.id ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-secondary"
                    />
                    <button
                      onClick={() => handleSaveEdit(rule.id)}
                      className="p-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="p-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-800 font-medium">
                    {rule.rule_text}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  onClick={() => handleToggle(rule)}
                  title={rule.is_enabled ? "Disable Rule" : "Enable Rule"}
                  className="text-gray-400 hover:text-secondary transition-colors"
                >
                  {rule.is_enabled ? (
                    <ToggleRight className="w-6 h-6 text-secondary" />
                  ) : (
                    <ToggleLeft className="w-6 h-6 text-gray-400" />
                  )}
                </button>
                <button
                  onClick={() => handleStartEdit(rule)}
                  className="p-1.5 text-gray-400 hover:text-secondary hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(rule.id)}
                  className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MemoryRules;
